import React from 'react';
import { Box, Typography } from '@mui/material';
import { darkTheme } from '../../contexts/ThemeManager/ThemeManager';

interface ScheduleLegendProps {
  isDarkMode: boolean;
  selectedSchedule?: any;
  accountType: 'owner' | 'employee';
  availabilityMode: boolean;
  legendFilters?: Record<string, boolean>;
  onToggleFilter?: (filterKey: string) => void;
}

const ScheduleLegend: React.FC<ScheduleLegendProps> = ({ selectedSchedule, isDarkMode, accountType, availabilityMode, legendFilters = {}, onToggleFilter }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1.5, sm: 2, md: 3 },
        mt: 3,
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}
    >
      {/* Fully Staffed or Your Shift */}
      {(<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.fullyStaffed !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('fullyStaffed')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: accountType === 'employee' ? 'primary.main' : `primary.light`,
            border: 1,
            borderColor: accountType === 'employee' ? 'primary.dark' : `primary.main`,
            opacity: legendFilters.fullyStaffed !== false ? 1 : 0.5,
            filter: legendFilters.fullyStaffed === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          {accountType === 'employee' ? 'Your Shift' : 'Fully Staffed'}
        </Typography>
      </Box>
      )}

      {/* Understaffed */}
      {accountType === 'owner' && (
        <>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              cursor: 'pointer',
              opacity: legendFilters.understaffed !== false ? 1 : 0.3,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => onToggleFilter?.('understaffed')}
          >
            <Box
              sx={{
                width: { xs: 12, sm: 14, md: 16 },
                height: { xs: 12, sm: 14, md: 16 },
                borderRadius: 1,
                bgcolor: 'info.light',
                border: 1,
                borderColor: 'info.main',
                opacity: legendFilters.understaffed !== false ? 1 : 0.5,
                filter: legendFilters.understaffed === false ? 'grayscale(100%)' : 'none',
                transition: 'all 0.2s'
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            >
              Understaffed
            </Typography>
          </Box>

          {/* Unstaffed */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              cursor: 'pointer',
              opacity: legendFilters.unstaffed !== false ? 1 : 0.3,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => onToggleFilter?.('unstaffed')}
          >
            <Box
              sx={{
                width: { xs: 12, sm: 14, md: 16 },
                height: { xs: 12, sm: 14, md: 16 },
                borderRadius: 1,
                bgcolor: 'error.light',
                border: 1,
                borderColor: 'error.main',
                opacity: legendFilters.unstaffed !== false ? 1 : 0.5,
                filter: legendFilters.unstaffed === false ? 'grayscale(100%)' : 'none',
                transition: 'all 0.2s'
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            >
              Unstaffed
            </Typography>
          </Box>
        </>
      )}

      {/* Other Shifts */}
      {accountType === 'employee' && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            opacity: legendFilters.available !== false ? 1 : 0.3,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => onToggleFilter?.('available')}
        >
          <Box
            sx={{
              width: { xs: 12, sm: 14, md: 16 },
              height: { xs: 12, sm: 14, md: 16 },
              borderRadius: 1,
              bgcolor: 'info.light',
              border: 1,
              borderColor: 'info.main',
              opacity: legendFilters.available !== false ? 1 : 0.5,
              filter: legendFilters.available === false ? 'grayscale(100%)' : 'none',
              transition: 'all 0.2s'
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
          >
            Other Available Shifts
          </Typography>
        </Box>
      )}

      {/* Unfinalized Schedule */}
      {accountType === 'owner' && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            opacity: legendFilters.unfinalized !== false ? 1 : 0.3,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => onToggleFilter?.('unfinalized')}
        >
          <Box
            sx={{
              width: { xs: 12, sm: 14, md: 16 },
              height: { xs: 12, sm: 14, md: 16 },
              borderRadius: 1,
              bgcolor: 'divider',
              border: 1,
              borderColor: 'text.primary',
              opacity: legendFilters.unfinalized !== false ? 1 : 0.5,
              filter: legendFilters.unfinalized === false ? 'grayscale(100%)' : 'none',
              transition: 'all 0.2s'
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
          >
            Unfinalized Schedule
          </Typography>
        </Box>
      )}

      {/* Holiday */}
      {!availabilityMode && (<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.holiday !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('holiday')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: isDarkMode ? 'rgba(106, 195, 255, 0.25)' : '#ffdfc5ff',
            border: 1,
            borderColor: 'text.secondary',
            opacity: legendFilters.holiday !== false ? 1 : 0.5,
            filter: legendFilters.holiday === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          Holiday
        </Typography>
      </Box>
      )}

      {/* Submit Next Schedule */}
      {!availabilityMode && (<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.nextSchedule !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('nextSchedule')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: selectedSchedule?.color+"6e",
            opacity: legendFilters.nextSchedule !== false ? 1 : 0.5,
            filter: legendFilters.nextSchedule === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          Next Schedule Start
        </Typography>
      </Box>
      )}

      {/* Available */}
      {availabilityMode && (<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.available !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('available')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: 'success.main',
            opacity: legendFilters.available !== false ? 1 : 0.5,
            filter: legendFilters.available === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          {'Availabile'}
        </Typography>
      </Box>
      )}

      {/* Unavailable */}
      {availabilityMode && (<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.unavailable !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('unavailable')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: isDarkMode ? 'error.main' : 'error.light',
            opacity: legendFilters.unavailable !== false ? 1 : 0.5,
            filter: legendFilters.unavailable === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          {'Unavailable'}
        </Typography>
      </Box>
      )}

      {/* SemiAvailable */}
      {availabilityMode && (<Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: 'pointer',
          opacity: legendFilters.semiAvailable !== false ? 1 : 0.3,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 0.8 }
        }}
        onClick={() => onToggleFilter?.('semiAvailable')}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: 1,
            bgcolor: isDarkMode ? 'warning.light' : darkTheme.palette.warning.light,
            opacity: legendFilters.semiAvailable !== false ? 1 : 0.5,
            filter: legendFilters.semiAvailable === false ? 'grayscale(100%)' : 'none',
            transition: 'all 0.2s'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          {'<7.5hrs Available'}
        </Typography>
      </Box>
      )}

      {/* Today */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1
        }}
      >
        <Box
          sx={{
            width: { xs: 12, sm: 14, md: 16 },
            height: { xs: 12, sm: 14, md: 16 },
            borderRadius: '50%',
            bgcolor: isDarkMode ? 'info.main' : 'primary.main'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
        >
          Today
        </Typography>
      </Box>
    </Box>
  );
};

export default ScheduleLegend;